import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useRoute } from '@react-navigation/native';
import {
  BodyText,
  PhoneLink,
  theme,
  TitleText,
  useBreakpointHelpers,
} from '@sweetgreen/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

import { useOrderStatusQuery } from './GraphQL/OrderStatus.query.generated';

export const OrderStatusContactUs = () => {
  const { currentBreakpoint } = useBreakpointHelpers();

  if (currentBreakpoint.isMobile) return <MobileContactUs />;

  return <DesktopContactUs />;
};

const DesktopContactUs = () => {
  const { t } = useLocalizationContext();
  const { phoneNumber, formattedPhoneNumber } = usePhoneNumber();

  return (
    <View style={[styles.contactUsContainerDesktop, styles.row]}>
      <BodyText style={styles.contactUsText}>
        <BodyText size={4} bold>
          {t('order-status-info.contact-us-question')}
        </BodyText>
        <PhoneLink
          protocol="sms"
          phoneNumber={phoneNumber}
          style={[styles.contactUsText, styles.contactUsCta]}
          alertLabel={t('order-status-info.sms-not-supported')}
          size={4}
        >
          {t('order-status-info.contact-us-cta')}
        </PhoneLink>
        <BodyText size={4}>
          {t('order-status-info.contact-us-answer', { formattedPhoneNumber })}
        </BodyText>
      </BodyText>
    </View>
  );
};

const MobileContactUs = () => {
  const { t } = useLocalizationContext();
  const { phoneNumber, formattedPhoneNumber } = usePhoneNumber();

  return (
    <>
      <View style={styles.contactUsDividerMobile} />
      <View style={styles.contactUsContainerMobile}>
        <TitleText style={styles.contactUsTitle} size={5}>
          {t('order-status-info.contact-us-question')}
        </TitleText>
        <BodyText style={styles.contactUsTextMobile} size={4}>
          {t('order-status-info.contact-us-answer-mobile-1')}
        </BodyText>
        <BodyText style={styles.contactUsTextMobile} size={4}>
          {t('order-status-info.contact-us-answer-mobile-2', {
            formattedPhoneNumber,
          })}
        </BodyText>
        <PhoneLink
          protocol="sms"
          phoneNumber={phoneNumber}
          style={styles.contactUsButtonMobile}
          size={4}
          alertLabel={t('order-status-info.sms-not-supported')}
        >
          {t('order-status-info.contact-us-cta')}
        </PhoneLink>
      </View>
    </>
  );
};

const FALLBACK_PHONE_NUMBER = '8555933527';
const SHORTCODE_PHONE_NUMBER = '798476';

const usePhoneNumber = () => {
  const { params } = useRoute();
  const { orderId } = params as { readonly orderId: string };
  const [{ data }] = useOrderStatusQuery({
    variables: { id: orderId },
    requestPolicy: 'cache-only',
  });
  const storePhoneNumber =
    (data?.orderStatus?.__typename === 'OrderStatus' &&
      data?.orderStatus?.order?.restaurant?.phone) ||
    FALLBACK_PHONE_NUMBER;

  const { formatPhoneNumber } = useLocalizationContext();
  const shouldUseShortCode = useOrderHelpShortCode();

  const phoneNumber = shouldUseShortCode
    ? SHORTCODE_PHONE_NUMBER
    : storePhoneNumber;

  const formattedPhoneNumber = formatPhoneNumber({ phoneNumber }).national;

  return {
    phoneNumber,
    formattedPhoneNumber,
  };
};

const useOrderHelpShortCode = () => {
  return useFeatureFlag('permanent-order-help-shortcode-enabled', {
    listenForChanges: true,
  });
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
  },
  contactUsContainerDesktop: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'flex-start',
    borderTopWidth: 1,
    borderTopColor: theme.colors.GREEN_1,
    backgroundColor: theme.colors.KHAKI_2,
    padding: theme.spacing.large,
  },
  contactUsContainerMobile: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'flex-start',
    borderRadius: theme.radius.medium,
    backgroundColor: theme.colors.KHAKI_2,
    padding: theme.spacing.large,
  },
  contactUsDividerMobile: {
    marginTop: theme.spacing.large,
    marginBottom: theme.spacing.xlarge,
    backgroundColor: theme.colors.NEUTRAL_3,
    height: 1,
    width: '100%',
  },
  contactUsTitle: {
    marginBottom: theme.spacing.small,
  },
  contactUsTextMobile: {
    lineHeight: 20,
    textAlign: 'center',
  },
  contactUsText: {
    lineHeight: 20,
  },
  contactUsCta: {
    marginHorizontal: theme.spacing.xsmall,
  },
  contactUsButtonMobile: {
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    borderRadius: 100,
    marginTop: theme.spacing.small + theme.spacing.xsmall,
    color: theme.colors.WHITE,
    backgroundColor: theme.colors.GREEN_2,
    textDecorationLine: 'none',
    paddingHorizontal: theme.spacing.large,
    paddingVertical: theme.spacing.small,
  },
});
